import { zodResolver } from "@hookform/resolvers/zod";
import { Close } from "@mui/icons-material";
import { Drawer, IconButton, Stack, Typography } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";

import {
  useCreateCompetitionEntryWithProfileMutation,
  useCurrentCompetitionUuid,
} from "@/hooks/competitions";
import { useCompetitionRoutesQuery } from "@/hooks/routes";
import { useCurrentVenueUuid } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import {
  CreateEntryFormFields,
  CreateEntryFormSchema,
  CreateEntryFormValues,
} from "./CreateEntryForm";

const Header = () => {
  const navigate = useNavigate();
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        padding: 1,
        paddingLeft: 2,
        borderBottom: "1px solid",
        borderColor: "neutral.300",
      }}
      spacing={1}
    >
      <Stack sx={{ flex: 1 }}>
        <Typography level="title-md">Add new competitor</Typography>
      </Stack>
      <IconButton
        onClick={() =>
          navigate({
            to: "..",
            from: "/gym/$venueUuid/events/$competitionUuid/competitors/create",
            replace: true,
          })
        }
      >
        <Close />
      </IconButton>
    </Stack>
  );
};

const CompetitorForm = () => {
  const methods = useForm<CreateEntryFormValues>({
    defaultValues: {
      mode: "lookup",
    },
    resolver: zodResolver(CreateEntryFormSchema),
  });
  const competitionUuid = useCurrentCompetitionUuid();
  const venueUuid = useCurrentVenueUuid();

  const { data: routes } = useCompetitionRoutesQuery(competitionUuid, venueUuid);

  const navigate = useNavigate();

  const mutation = useCreateCompetitionEntryWithProfileMutation();

  const onSubmit = (values: CreateEntryFormValues) => {
    if (!competitionUuid || !routes) {
      return;
    }
    const matchingRoutes = routes.filter((route) =>
      route.competitionRouteGroups.some((crg) => crg.categories.includes(values.category)),
    );
    const attemptValues = values.attempts ?? {};
    const attempts = matchingRoutes
      .filter((route) => attemptValues[route.uuid]?.count !== undefined)
      .map((route) => ({
        route: route.uuid,
        success: attemptValues[route.uuid].success || false,
        count: attemptValues[route.uuid].count,
        segmentsCount: attemptValues[route.uuid].segmentsCount ?? null,
        segmentTriesCount: attemptValues[route.uuid].segmentTriesCount ?? null,
      }));

    return mutation.mutateAsync(
      {
        profile:
          values.mode === "create" && values.profile
            ? {
                ...values.profile,
                email: values.profile.email || null,
              }
            : undefined,
        user: values.mode === "lookup" ? values.user : undefined,
        category: values.category,
        attempts,
        competitionUuid,
      },
      {
        onError: (error) => {
          handleFormError(error, methods);
        },
        onSuccess: (entry) => {
          navigate({
            from: "/gym/$venueUuid/events/$competitionUuid/competitors/create",
            to: "../$userUuid",
            replace: true,
            params: {
              userUuid: entry.user.uuid,
            },
          });
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <Stack
        onSubmit={methods.handleSubmit(onSubmit)}
        sx={{ flex: 1 }}
        component="form"
        autoComplete="off"
      >
        <CreateEntryFormFields />
      </Stack>
    </FormProvider>
  );
};

export default function CreateEntrySidebar() {
  return (
    <Drawer open anchor="right" sx={{ ".MuiDrawer-content": { minWidth: 480 } }}>
      <Header />
      <CompetitorForm />
    </Drawer>
  );
}
